import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'

// Store imports
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'

// Custom hooks
import { useAuthSync } from '@/hooks/use-auth-sync'

// Layout components (to be created)
import { DashboardLayout } from '@/components/layout/dashboard-layout'
import { PublicLayout } from '@/components/layout/public-layout'

// Page components
import { Dashboard } from '@/pages/dashboard'
import { Editor } from '@/pages/editor'
import { KnowledgeManagement } from '@/pages/knowledge'
import { SearchPage } from '@/pages/search'
import { PublicKnowledgeBase } from '@/pages/public'
import { Admin } from '@/pages/admin'
import { Storage } from '@/pages/storage/index'
import { ImportSourcesAdmin } from '@/pages/admin/import-sources'
import { SystemPromptsAdmin } from '@/pages/admin/system-prompts'
import { EmailDrafter } from '@/pages/admin/email-drafter'
import { EmailQADashboard } from '@/pages/admin/email-qa-dashboard'
import { QATestProtocol } from '@/pages/admin/qa-test-protocol'
import { QAReview } from '@/pages/admin/qa-review'
import { EmailDrafterTickets } from '@/pages/emaildrafter-tickets'
import { ApiDocs } from '@/pages/api-docs'
import { ApiDocsStandalone } from '@/pages/api-docs-standalone'
import { Login } from '@/pages/auth/login'
import { Register } from '@/pages/auth/register'
import { ResetPassword } from '@/pages/auth/reset-password'
import { TicketSystem } from '@/pages/ticket-system'
import { Profile } from '@/pages/profile'
import { Settings } from '@/pages/settings'
import { NotFound } from '@/pages/not-found'

// UI components (to be created)
import { LoadingSpinner } from '@/components/ui/loading'
import { Toaster } from '@/components/ui/toaster'
import { ErrorBoundary } from '@/components/ui/error-boundary'

function App() {
  const { checkAuth, isLoading: authLoading, user } = useAuthStore()
  const { theme, setTheme } = useUIStore()

  // Initialize auth sync for preferences
  useAuthSync()

  // Initialize authentication on app start
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Apply theme on mount and when theme changes
  useEffect(() => {
    const applyTheme = () => {
      const root = document.documentElement

      if (theme === 'dark') {
        root.classList.add('dark')
      } else if (theme === 'light') {
        root.classList.remove('dark')
      } else {
        // System theme
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        if (prefersDark) {
          root.classList.add('dark')
        } else {
          root.classList.remove('dark')
        }
      }
    }

    applyTheme()

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleThemeChange = () => {
      if (theme === 'system') {
        applyTheme()
      }
    }

    mediaQuery.addEventListener('change', handleThemeChange)
    return () => mediaQuery.removeEventListener('change', handleThemeChange)
  }, [theme])

  return (
    <ErrorBoundary>
      <div className="min-h-screen">
        <Routes>
          {/* Public EmailDrafter Tickets route - No auth required - MUST BE FIRST */}
          <Route path="/emaildrafter" element={<EmailDrafterTickets />} />

          {/* Public routes - Always accessible */}
          <Route path="/public/*" element={
            <PublicLayout>
              <Routes>
                <Route index element={<PublicKnowledgeBase />} />
                <Route path="search" element={<PublicKnowledgeBase />} />
                <Route path="category/:category" element={<PublicKnowledgeBase />} />
              </Routes>
            </PublicLayout>
          } />

          {/* Authentication routes */}
          <Route path="/auth/*" element={
            authLoading ? (
              <div className="min-h-screen bg-gray-50 dark:bg-background flex items-center justify-center">
                <div className="text-center">
                  <LoadingSpinner size="lg" />
                  <p className="mt-4 text-muted-foreground">Mission Solar Knowledge Manager wird geladen...</p>
                </div>
              </div>
            ) : user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Routes>
                <Route path="login" element={<Login />} />
                <Route path="register" element={<Register />} />
                <Route path="reset-password" element={<ResetPassword />} />
                <Route index element={<Navigate to="/auth/login" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            )
          } />

          {/* Protected routes */}
          <Route path="/*" element={
            authLoading ? (
              <div className="min-h-screen bg-gray-50 dark:bg-background flex items-center justify-center">
                <div className="text-center">
                  <LoadingSpinner size="lg" />
                  <p className="mt-4 text-muted-foreground">Mission Solar Knowledge Manager wird geladen...</p>
                </div>
              </div>
            ) : user ? (
              <DashboardLayout>
                <Routes>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="knowledge/*" element={<KnowledgeManagement />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="editor" element={<Editor />} />
                  <Route path="editor/:id" element={<Editor />} />
                  <Route path="storage" element={<Storage />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="admin" element={<Admin />} />
                  <Route path="admin/import-sources" element={<ImportSourcesAdmin />} />
                  <Route path="admin/system-prompts" element={<SystemPromptsAdmin />} />
                  <Route path="admin/email-drafter" element={<EmailDrafter />} />
                  <Route path="admin/email-qa" element={<EmailQADashboard />} />
                  <Route path="admin/qa-test-protocol" element={<QATestProtocol />} />
                  <Route path="admin/qa-review" element={<QAReview />} />
                  <Route path="api-docs" element={<ApiDocs />} />
                  <Route path="api-docs-standalone" element={<ApiDocsStandalone />} />
                  <Route path="ticket-system" element={<TicketSystem />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </DashboardLayout>
            ) : (
              <Navigate to="/auth/login" replace />
            )
          } />

          {/* Catch-all route for any unmatched paths */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        {/* Global toast notifications */}
        <Toaster />
      </div>
    </ErrorBoundary>
  )
}

export default App